import { useState } from 'react';
import { Card } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Sparkles, AlertTriangle, Info } from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';

interface CleaningRules {
  remove_columns?: string[];
  normalize?: Record<string, boolean>;
  exclude_from_analysis?: string[];
}

const NORMALIZE_OPTIONS = [
  {
    id: 'currency',
    label: 'Normalize Currency Values',
    description: 'Strips currency symbols (₦, $, €) and converts to numbers for accurate calculations.',
    why: 'Without this, "₦50,000" and "50000" would be treated as different values.',
  },
  {
    id: 'dates',
    label: 'Standardize Date Formats',
    description: 'Converts all dates to a consistent format (ISO 8601) for sorting and filtering.',
    why: 'Mixed formats like "01/15/2024" and "2024-01-15" cause analysis errors.',
  },
  {
    id: 'text',
    label: 'Clean Text Fields',
    description: 'Trims whitespace, fixes casing, and removes special characters from text.',
    why: '"  John DOE " and "john doe" would be counted as separate entries.',
  },
  {
    id: 'duplicates',
    label: 'Remove Duplicate Rows',
    description: 'Identifies and removes exact or near-duplicate records.',
    why: 'Duplicate records inflate counts and distort averages in reports.',
  },
  {
    id: 'missing_values',
    label: 'Handle Missing Values',
    description: 'Fills gaps using statistical methods or flags incomplete rows.',
    why: 'Missing data can cause prediction models to produce unreliable results.',
  },
];

const EXCLUDE_OPTIONS = [
  { id: 'notes', label: 'Internal Notes / Comments', description: 'Free-text notes that add noise to analysis' },
  { id: 'reviews', label: 'Customer Reviews', description: 'Unstructured text that requires NLP processing' },
  { id: 'internal_ids', label: 'Internal System IDs', description: 'Technical identifiers with no analytical value' },
  { id: 'attachments', label: 'File Attachments / URLs', description: 'Binary references that cannot be analyzed numerically' },
];

interface Props {
  rules: CleaningRules;
  industry: string | null;
  onUpdate: (rules: CleaningRules) => void;
}

export const CleaningRulesStep = ({ rules, industry, onUpdate }: Props) => {
  const normalize = rules.normalize || {};
  const excludeList = rules.exclude_from_analysis || [];

  const toggleNormalize = (key: string) => {
    onUpdate({
      ...rules,
      normalize: { ...normalize, [key]: !normalize[key] },
    });
  };

  const toggleExclude = (key: string) => {
    const updated = excludeList.includes(key)
      ? excludeList.filter(k => k !== key)
      : [...excludeList, key];
    onUpdate({ ...rules, exclude_from_analysis: updated });
  };

  return (
    <div className="space-y-6">
      <div>
        <div className="flex items-center gap-2 mb-1">
          <Sparkles className="h-5 w-5 text-primary" />
          <h3 className="font-semibold text-lg">How should we tidy your data?</h3>
        </div>
        <p className="text-sm text-muted-foreground mb-4">
          Select how the AI should clean your data. These rules apply automatically to every workflow run.
        </p>
      </div>

      {/* Normalize options */}
      <Card className="p-4">
        <h4 className="font-medium mb-3 text-sm">Normalization & Formatting</h4>
        <div className="space-y-4">
          {NORMALIZE_OPTIONS.map((opt) => (
            <div key={opt.id} className="flex items-start gap-3">
              <Checkbox
                id={`norm-${opt.id}`}
                checked={!!normalize[opt.id]}
                onCheckedChange={() => toggleNormalize(opt.id)}
              />
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <Label htmlFor={`norm-${opt.id}`} className="font-medium text-sm cursor-pointer">
                    {opt.label}
                  </Label>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger>
                        <Info className="h-3.5 w-3.5 text-muted-foreground" />
                      </TooltipTrigger>
                      <TooltipContent side="right" className="max-w-xs">
                        <p className="text-xs"><strong>Why?</strong> {opt.why}</p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
                <p className="text-xs text-muted-foreground mt-0.5">{opt.description}</p>
              </div>
            </div>
          ))}
        </div>
      </Card>

      {/* Exclude from analysis */}
      <Card className="p-4">
        <h4 className="font-medium mb-3 text-sm">Exclude from Analysis</h4>
        <p className="text-xs text-muted-foreground mb-3">
          These column types will be preserved in your data but excluded from AI predictions and charts.
        </p>
        <div className="space-y-3">
          {EXCLUDE_OPTIONS.map((opt) => (
            <div key={opt.id} className="flex items-start gap-3">
              <Checkbox
                id={`excl-${opt.id}`}
                checked={excludeList.includes(opt.id)}
                onCheckedChange={() => toggleExclude(opt.id)}
              />
              <div>
                <Label htmlFor={`excl-${opt.id}`} className="font-medium text-sm cursor-pointer">
                  {opt.label}
                </Label>
                <p className="text-xs text-muted-foreground">{opt.description}</p>
              </div>
            </div>
          ))}
        </div>
      </Card>

      {industry && (
        <div className="flex items-center gap-2 p-3 bg-primary/5 rounded-lg border border-primary/10">
          <AlertTriangle className="h-4 w-4 text-primary" />
          <p className="text-xs text-muted-foreground">
            <strong className="text-foreground">Industry-aware:</strong> The AI will apply additional {industry}-specific cleaning patterns automatically.
          </p>
        </div>
      )}
    </div>
  );
};
